import React, { useState, useMemo } from "react";
import { CaretLeft, CaretRight, CalendarBlank } from "@phosphor-icons/react";

const MONTHS = {
  de: ["Januar", "Februar", "März", "April", "Mai", "Juni", "Juli", "August", "September", "Oktober", "November", "Dezember"],
  en: ["January", "February", "March", "April", "May", "June", "July", "August", "September", "October", "November", "December"],
};
const DOW = { de: ["Mo", "Di", "Mi", "Do", "Fr", "Sa", "So"], en: ["Mon", "Tue", "Wed", "Thu", "Fri", "Sat", "Sun"] };

export default function AdminCalendar({ bookings = [], lang = "de" }) {
  const de = lang === "de";
  const now = new Date();
  const todayStr = now.toISOString().slice(0, 10);
  const [ym, setYm] = useState({ y: now.getFullYear(), m: now.getMonth() });
  const [selected, setSelected] = useState(todayStr);

  const byDate = useMemo(() => {
    const map = {};
    for (const b of bookings) {
      if (!b.date) continue;
      (map[b.date] = map[b.date] || []).push(b);
    }
    for (const k in map) map[k].sort((a, b) => (a.time || "").localeCompare(b.time || ""));
    return map;
  }, [bookings]);

  const pad = (n) => String(n).padStart(2, "0");
  const dateStr = (d) => `${ym.y}-${pad(ym.m + 1)}-${pad(d)}`;
  const move = (delta) => { let m = ym.m + delta, y = ym.y; if (m < 0) { m = 11; y--; } if (m > 11) { m = 0; y++; } setYm({ y, m }); };
  const money = (v) => `CHF ${Number(v || 0).toFixed(2)}`;

  const startDow = (new Date(ym.y, ym.m, 1).getDay() + 6) % 7;
  const daysInMonth = new Date(ym.y, ym.m + 1, 0).getDate();
  const cells = [...Array(startDow).fill(null), ...Array.from({ length: daysInMonth }, (_, i) => i + 1)];
  const dayList = byDate[selected] || [];

  return (
    <div className="space-y-6" data-testid="admin-calendar">
      <div className="border border-[#E9E5DE] p-6">
        <div className="flex items-center justify-between mb-5">
          <h3 className="font-heading text-lg tracking-tight flex items-center gap-2"><CalendarBlank size={18} className="text-[#EF5A1E]" /> {MONTHS[lang][ym.m]} {ym.y}</h3>
          <div className="flex items-center gap-2">
            <button data-testid="cal-prev" onClick={() => move(-1)} className="p-2 border border-[#E9E5DE] hover:bg-[#F5F2ED] transition-colors"><CaretLeft size={16} /></button>
            <button data-testid="cal-next" onClick={() => move(1)} className="p-2 border border-[#E9E5DE] hover:bg-[#F5F2ED] transition-colors"><CaretRight size={16} /></button>
          </div>
        </div>
        <div className="grid grid-cols-7 gap-1.5">
          {DOW[lang].map((d) => <div key={d} className="text-center font-body text-[10px] uppercase tracking-widest text-[#5A607A] py-1">{d}</div>)}
          {cells.map((d, i) => {
            if (!d) return <div key={`e${i}`} />;
            const ds = dateStr(d);
            const count = (byDate[ds] || []).length;
            const isSel = ds === selected;
            const isToday = ds === todayStr;
            return (
              <button key={ds} data-testid={`cal-day-${ds}`} onClick={() => setSelected(ds)}
                className={`relative aspect-square flex flex-col items-center justify-center border text-sm transition-colors duration-150 ${isSel ? "border-[#EF5A1E] bg-[#EEF1FF]" : "border-[#E9E5DE] hover:bg-[#F5F2ED]"}`}>
                <span className={isToday ? "text-[#EF5A1E] font-bold" : ""}>{d}</span>
                {count > 0 && <span className="mt-1 min-w-[18px] px-1 rounded-full bg-[#EF5A1E] text-white text-[10px] font-semibold">{count}</span>}
              </button>
            );
          })}
        </div>
      </div>

      <div className="border border-[#E9E5DE] p-6" data-testid="cal-daylist">
        <h3 className="font-heading text-lg tracking-tight mb-1">{de ? "Fahrten am" : "Transfers on"} {selected}</h3>
        <p className="font-body text-sm text-[#5A607A] mb-5">{dayList.length} {de ? (dayList.length === 1 ? "Transfer" : "Transfers") : (dayList.length === 1 ? "transfer" : "transfers")}</p>
        {dayList.length === 0 ? (
          <p className="font-body text-sm text-[#5A607A]">{de ? "Keine Fahrten an diesem Tag." : "No transfers on this day."}</p>
        ) : (
          <div className="space-y-3">
            {dayList.map((b) => (
              <div key={b.id} className="flex flex-wrap items-center gap-4 border border-[#E9E5DE] rounded-lg p-4" data-testid={`cal-trip-${b.reference}`}>
                <div className="text-center shrink-0 w-16">
                  <p className="font-heading font-bold text-xl text-[#0B1533]">{b.time}</p>
                  <p className="font-body text-[10px] uppercase tracking-widest text-[#EF5A1E]">{b.reference}</p>
                </div>
                <div className="flex-1 min-w-[200px]">
                  <p className="font-body text-sm text-[#0B1533]">{b.pickup_name || "ZRH"} → {b.dropoff_name || b.zone_name}</p>
                  <p className="font-body text-xs text-[#5A607A] mt-0.5">{b.name} · {b.phone} · {b.passengers}p{b.flight_number ? ` · ${b.flight_number}` : ""}</p>
                </div>
                <div className="text-right shrink-0">
                  <p className="font-body text-sm font-medium">{money(b.total)}</p>
                  <p className="font-body text-[10px] uppercase tracking-wider text-[#5A607A]">{b.vehicle_class} · {b.status}</p>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
